import type { PublicacaoDto } from "@base/contracts";

/**
 * Helpers puros da agenda (dia / semana / mês).
 * Semana começa na segunda-feira.
 */

export type VistaDaAgenda = "dia" | "semana" | "mes";

const ORDEM_REDE: Record<PublicacaoDto["plataforma"], number> = {
  instagram: 0,
  facebook: 1,
  twitter: 2,
  tiktok: 3,
};

export type GrupoPublicacaoAgenda = {
  readonly chave: string;
  readonly itens: readonly PublicacaoDto[];
  readonly representativo: PublicacaoDto;
};

/**
 * Junta posts criados juntos (mesmo nome, tipo, horário e legenda).
 * Uma publicação por rede no banco; um card na agenda.
 */
export function chaveGrupoPublicacao(p: PublicacaoDto): string {
  const quando = p.agendarPara ?? p.criadoEm;
  const minuto = quando.slice(0, 16);
  return [p.nome.trim().toLowerCase(), p.tipo, minuto, p.legenda.trim()].join(
    "|",
  );
}

export function agruparPublicacoesAgenda(
  itens: readonly PublicacaoDto[],
): GrupoPublicacaoAgenda[] {
  const mapa = new Map<string, PublicacaoDto[]>();
  for (const p of itens) {
    const chave = chaveGrupoPublicacao(p);
    const lista = mapa.get(chave) ?? [];
    lista.push(p);
    mapa.set(chave, lista);
  }
  return [...mapa.entries()]
    .map(([chave, grupo]) => {
      const itens = [...grupo].sort(
        (a, b) => ORDEM_REDE[a.plataforma] - ORDEM_REDE[b.plataforma],
      );
      return { chave, itens, representativo: itens[0]! };
    })
    .sort((a, b) =>
      (a.representativo.agendarPara ?? a.representativo.criadoEm).localeCompare(
        b.representativo.agendarPara ?? b.representativo.criadoEm,
      ),
    );
}

export function falhasDoGrupo(
  itens: readonly PublicacaoDto[],
): PublicacaoDto[] {
  return itens.filter((p) => p.status === "falhou");
}

export function inicioDoDia(ref: Date = new Date()): Date {
  const d = new Date(ref);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function inicioDaSemana(ref: Date = new Date()): Date {
  const d = inicioDoDia(ref);
  const dia = d.getDay();
  const diff = dia === 0 ? -6 : 1 - dia;
  d.setDate(d.getDate() + diff);
  return d;
}

export function inicioDoMes(ref: Date = new Date()): Date {
  const d = inicioDoDia(ref);
  d.setDate(1);
  return d;
}

/** Chave local YYYY-MM-DD (evita virada de dia por UTC). */
export function chaveDoDia(ref: Date): string {
  const y = ref.getFullYear();
  const m = String(ref.getMonth() + 1).padStart(2, "0");
  const dia = String(ref.getDate()).padStart(2, "0");
  return `${y}-${m}-${dia}`;
}

export function ancoraDaVista(vista: VistaDaAgenda, ref: Date = new Date()): Date {
  if (vista === "dia") return inicioDoDia(ref);
  if (vista === "semana") return inicioDaSemana(ref);
  return inicioDoMes(ref);
}

/**
 * Ao trocar o toggle da agenda: "dia" sempre abre hoje.
 * Semana/mês preservam o período a partir da âncora atual.
 */
export function ancoraAoTrocarVista(
  proxima: VistaDaAgenda,
  ancoraAtual: Date,
  agora: Date = new Date(),
): Date {
  if (proxima === "dia") return ancoraDaVista("dia", agora);
  return ancoraDaVista(proxima, ancoraAtual);
}

/** Início inclusivo e fim exclusivo do intervalo consultado na API. */
export function intervaloDaVista(
  vista: VistaDaAgenda,
  ancora: Date,
): { readonly de: Date; readonly ate: Date } {
  const de = ancoraDaVista(vista, ancora);
  const ate = new Date(de);
  if (vista === "dia") {
    ate.setDate(ate.getDate() + 1);
  } else if (vista === "semana") {
    ate.setDate(ate.getDate() + 7);
  } else {
    ate.setMonth(ate.getMonth() + 1);
  }
  return { de, ate };
}

export function avancarPeriodo(
  vista: VistaDaAgenda,
  ancora: Date,
  delta: number,
): Date {
  const base = ancoraDaVista(vista, ancora);
  const proximo = new Date(base);
  if (vista === "dia") {
    proximo.setDate(proximo.getDate() + delta);
  } else if (vista === "semana") {
    proximo.setDate(proximo.getDate() + delta * 7);
  } else {
    proximo.setMonth(proximo.getMonth() + delta);
  }
  return ancoraDaVista(vista, proximo);
}

export function diasDaSemana(ancora: Date): readonly Date[] {
  const inicio = inicioDaSemana(ancora);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(inicio);
    d.setDate(inicio.getDate() + i);
    return d;
  });
}

/**
 * Grade do mês: semanas de segunda a domingo cobrindo o mês da âncora.
 */
export function celulasDoMes(ancora: Date): readonly Date[] {
  const primeiro = inicioDoMes(ancora);
  const inicioGrade = inicioDaSemana(primeiro);
  const ultimo = new Date(primeiro);
  ultimo.setMonth(ultimo.getMonth() + 1);
  ultimo.setDate(0);
  const fimGrade = inicioDaSemana(ultimo);
  fimGrade.setDate(fimGrade.getDate() + 6);

  const celulas: Date[] = [];
  const cursor = new Date(inicioGrade);
  while (cursor.getTime() <= fimGrade.getTime()) {
    celulas.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return celulas;
}

export function rotuloDoPeriodo(vista: VistaDaAgenda, ancora: Date): string {
  if (vista === "dia") {
    return ancora.toLocaleDateString("pt-BR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }
  if (vista === "semana") {
    const dias = diasDaSemana(ancora);
    const primeiro = dias[0]!;
    const ultimo = dias[6]!;
    const mesmoMes = primeiro.getMonth() === ultimo.getMonth();
    const ini = primeiro.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: mesmoMes ? undefined : "short",
    });
    const fim = ultimo.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return `${ini} a ${fim}`;
  }
  return ancora.toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  });
}

export function mesmoDia(a: Date, b: Date): boolean {
  return chaveDoDia(a) === chaveDoDia(b);
}

export function mesmoMes(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}
